import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { STORE_API } from "./CartContext";

const Ctx = createContext(null);
const WKEY = "bf_wishlist";
const api = axios.create({ baseURL: `${STORE_API}/account`, withCredentials: true });

export function AccountProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WKEY)) || []; } catch { return []; }
  });
  const synced = useRef(false);

  useEffect(() => { localStorage.setItem(WKEY, JSON.stringify(wishlist)); }, [wishlist]);

  const syncWishlist = useCallback(async (u, local) => {
    const merged = [...new Set([...(u.wishlist || []), ...local])];
    setWishlist(merged);
    if (merged.length !== (u.wishlist || []).length) await api.put("/wishlist", { product_ids: merged }).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data);
      if (!synced.current) { synced.current = true; syncWishlist(data, JSON.parse(localStorage.getItem(WKEY) || "[]")); }
    } catch { setUser(null); }
  }, [syncWishlist]);

  useEffect(() => {
    // Skip while returning from Google sign-in; AccountCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);

  const login = useCallback((next = "/account") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchange = useCallback(async (sessionId) => {
    const { data } = await api.post("/session", null, { headers: { "X-Session-ID": sessionId } });
    setUser(data);
    synced.current = true;
    await syncWishlist(data, JSON.parse(localStorage.getItem(WKEY) || "[]"));
    return data;
  }, [syncWishlist]);

  const logout = useCallback(async () => {
    await api.post("/logout").catch(() => {});
    setUser(null);
    setWishlist([]);
    localStorage.removeItem(WKEY);
    synced.current = false;
  }, []);

  const toggleWish = useCallback((id) => {
    setWishlist((list) => {
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      if (user) api.put("/wishlist", { product_ids: next }).catch(() => {});
      return next;
    });
  }, [user]);

  const value = useMemo(() => ({ user, wishlist, login, logout, exchange, toggleWish, refresh, api }), [user, wishlist, login, logout, exchange, toggleWish, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAccount = () => useContext(Ctx);
